// The clip editor: Live's Clip View for one audio clip, made for a thumb as
// much as for a mouse. Three ways to be on screen (`presentation`):
//
//   - Docked (a wider screen): under the timeline or the pads, as Live's Clip
//     View is. The room keeps it minimized to a strip until a clip is
//     double-clicked or held; `onMinimize` folds it back down.
//   - A sheet (a phone): in the column under the timeline or the pads, at a
//     height the page sets (hooks/useSheetSnap) — a tap on a clip loads it
//     into the sheet's strip, a double-tap or a hold opens it. The page owns the handle; the ✕ here
//     puts it away.
//   - A modal: the sheet's column layout, over the page with a backdrop,
//     pulled down to close. Kept for pages with no column to sit in.
//
// What a person sees, top to bottom:
//   - the clip: colour, name, Undo/Redo, Close
//   - the waveform, drawn warped onto the beat grid, with its warp markers
//     (yellow) and the hits in the audio (grey ticks), and a toolbar for what
//     a phone has no modifier keys for
//   - four tabs, Note-style, each a handful of big controls (Koala's habit of
//     one chunky button per idea):
//       Warp   Off / On, Live's six modes and their knobs, the tempo, ÷2 ×2, Tap tempo
//       Pitch  a big readout, ± a semitone, interval pads (octave, fifth,
//              fourth), a fine-tune slider
//       Loop   on/off, length pads (a beat … 8 bars), nudge the brace
//       Clip   name, colour, mute, Bounce in place
//   - on a phone, a big Play pill along the bottom
//
// Every change is one intent through the edit builders, so undo, sync and
// the room's validation all work as they do on the timeline. A marker drag
// or a slider is drawn locally and proposed once, on release. The audio
// re-renders on the stretch Worker after that, and the old render plays
// until the new one is in.

import { renderStatus } from "@shared/sync/clip-render.js";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from "react";
import {
  ChevronLeft,
  ChevronRight,
  Hand,
  Magnet,
  Maximize2,
  Minus,
  Plus,
  Repeat,
  Trash2,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type { CanonicalRoom } from "@shared/sync/state.js";
import type { Intent } from "@shared/sync/intents.js";
import { MAX_DETUNE, MAX_PRO_ENVELOPE, MAX_TRANSPOSE, MIN_PRO_ENVELOPE, WARP_PRESERVE_DIVISIONS, WARP_SETTINGS_DEFAULTS, type WarpSettingsData } from "@shared/sync/protocol.js";
import {
  findClip,
  setClipFields,
  setClipLoop,
  setClipLooping,
  setClipPitch,
  setClipWarpSettings,
  setClipWarp,
  setClipWarpMarkers,
} from "@web-shared/lib/edits";
import { beatAtFileSeconds, fileSecondsAt, type WarpMarker } from "@web-shared/lib/warp";
import type { Onset } from "@web-shared/lib/stretch/onsets";
import { prepareCanvas } from "@web-shared/lib/waveform";
import { cn } from "@web-shared/lib/utils";
import { usePref } from "@web-shared/hooks/usePref";
import {
  addMarkerNear,
  beatToX,
  effectiveMarkers,
  insertMarkerAtBeat,
  insertMarkerAtSec,
  markerNear,
  moveMarkerBeat,
  moveMarkerSec,
  nearestTransient,
  pinchView,
  removeMarker,
  segmentBpm,
  snapBeat,
  warpChoiceOf,
  warpFor,
  warpOnKeeping,
  WARP_CHOICES,
  warpStraightFrom,
  xToBeat,
  zoomAt,
  type ClipView,
  type WarpChoice,
} from "@web-shared/components/clip-editor/interactions";
import { MARKER_H, paintEditor, RULER_H, type EditorPaint } from "@web-shared/components/clip-editor/draw";
import {
  ACTIVE,
  BigChoice,
  buzz,
  ClipPanel,
  EditorHeader,
  EditorTabs,
  Field,
  IDLE,
  lengthLabel,
  Segmented,
  Stepper,
  TextButton,
  ToolButton,
  type EditorHistory,
} from "@web-shared/components/clip-editor/chrome";

/** What the editor needs from the player. */
export interface ClipEditorPlayer {
  peaks: (url: string, buckets: number, offsetSec?: number, durationSec?: number) => Float32Array | null;
  ensurePeaks: (url: string, priority?: "now" | "later") => void;
  bufferFor: (url: string) => Promise<AudioBuffer>;
  onsetsFor: (url: string) => Promise<Onset[]>;
  /** Bumps as objects decode. */
  decodedVersion: number;
}

export interface ClipEditorProps {
  room: CanonicalRoom;
  clipId: string;
  /** The room's tempo. */
  tempo: number;
  beatsPerBar?: number;
  /** The clip's object, fetchable. */
  url: string | undefined;
  /** The clip's colour, `#rrggbb`. */
  color: string;
  player: ClipEditorPlayer;
  /** Where the transport is in the clip, in clip beats; null when outside it. */
  playheadBeat: number | null;
  propose: (intents: Intent[]) => void;
  onClose: () => void;
  /** Docked: fold the editor down to the page's one-line strip, clip still
   * loaded. Absent ⇒ no minimize button. */
  onMinimize?: () => void;
  /** A sheet over the room (a phone) rather than docked under it. */
  /** A phone's sheet over the page; shorthand for `presentation: "modal"`. */
  modal?: boolean;
  /** Where the editor sits — see the file's header. Default docked. */
  presentation?: "docked" | "sheet" | "modal";
  /** Hear the clip in the arrangement, from its start, looping; absent ⇒
   * no Preview button. */
  onPreview?: () => void;
  previewing?: boolean;
  /** The room's undo, so a phone doesn't have to leave the sheet for it. */
  history?: EditorHistory;
  /** Docked: the clip's actions (copy, split, delete…), in the header. */
  actions?: ReactNode;
  /** Bounce the clip in place: its audio replaced by what you hear, and the
   * original kept as a backup in the Session View. Resolves to a sentence saying what
   * happened; rejects with what went wrong. Absent ⇒ no button. */
  onBounce?: () => Promise<string>;
  /** Called when an edit lands that Live cannot represent (warp markers, the
   * mode knobs): the page bounces the clip once the editor lets go of it. */
  onWarpEdit?: () => void;
  /** Download this clip (the page asks which form). Absent ⇒ no button. */
  onDownload?: () => void;
}

const MIN_LOOP_BEATS = 0.25;

type Tab = "warp" | "pitch" | "loop" | "clip";
const TABS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: "warp", label: "Warp" },
  { value: "pitch", label: "Pitch" },
  { value: "loop", label: "Loop" },
  { value: "clip", label: "Clip" },
];
const isTab = (v: unknown): v is Tab => v === "warp" || v === "pitch" || v === "loop" || v === "clip";

/** The pitch pads: the intervals a sampler player reaches for. */
const INTERVALS: ReadonlyArray<{ st: number; label: string; name: string }> = [
  { st: -12, label: "−12", name: "Octave down" },
  { st: -7, label: "−7", name: "Fifth down" },
  { st: -5, label: "−5", name: "Fourth down" },
  { st: 0, label: "0", name: "Original pitch" },
  { st: 5, label: "+5", name: "Fourth up" },
  { st: 7, label: "+7", name: "Fifth up" },
  { st: 12, label: "+12", name: "Octave up" },
];

/** Whether the main pointer is a finger. Read live: a laptop with a
 * touchscreen switches when it is used. */
function useCoarsePointer(): boolean {
  const query = "(pointer: coarse)";
  const [coarse, setCoarse] = useState(() => typeof window !== "undefined" && window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const on = () => setCoarse(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return coarse;
}

export function ClipEditor({
  room,
  clipId,
  tempo,
  beatsPerBar = 4,
  url,
  color,
  player,
  playheadBeat,
  propose,
  onClose,
  onMinimize,
  modal: modalProp = false,
  presentation = modalProp ? "modal" : "docked",
  onPreview,
  previewing = false,
  history,
  onBounce,
  onWarpEdit,
  onDownload,
  actions,
}: ClipEditorProps) {
  // `modal` is the overlay (backdrop, fixed, pull-to-close); `column` is the
  // thumb layout both the modal and the in-column sheet use.
  const modal = presentation === "modal";
  const column = presentation !== "docked";
  const clip = findClip(room, clipId);
  const [file, setFile] = useState<{ sec: number } | null>(null);
  const [transients, setTransients] = useState<number[]>([]);
  const [failed, setFailed] = useState(false);
  const { bufferFor, onsetsFor, ensurePeaks } = player;
  const [tab, setTab] = usePref<Tab>("seedbed.clipEditor.tab", "warp", isTab);
  // Swipe the sheet down by its grab bar or header to close it (a phone's habit).
  const [pull, setPull] = useState(0);
  const pullFrom = useRef<number | null>(null);

  useEffect(() => {
    setFile(null);
    setTransients([]);
    setFailed(false);
    if (!url) return;
    let live = true;
    ensurePeaks(url);
    bufferFor(url)
      .then((b) => {
        if (!live) return;
        setFile({ sec: b.duration });
        return onsetsFor(url).then((o) => {
          if (live) setTransients(o.map((t) => t.sample / b.sampleRate));
        });
      })
      .catch((err: unknown) => {
        console.warn("[clip editor] could not load", url, err);
        if (live) setFailed(true);
      });
    return () => {
      live = false;
    };
  }, [url, bufferFor, onsetsFor, ensurePeaks]);

  // A modal sheet holds the page still behind it, and takes focus so Esc
  // and the screen reader land in it.
  const sheetRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!modal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    sheetRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = prev;
    };
  }, [modal]);

  // Esc closes from anywhere in the editor (the waveform handles its own
  // first, to drop a selection).
  const onSheetKey = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      onClose();
    }
  };

  if (!clip || clip.clipKind === "midi") return null;

  const warped = clip.isWarped;
  const choice = warpChoiceOf(warped, clip.warpModeValue);
  const sourceBpm = clip.sourceTempo ?? tempo;
  const ownMarkers = !!clip.warpMarkers && clip.warpMarkers.length >= 2;
  const one = (intent: Intent | null) => {
    if (intent) {
      buzz(6);
      propose([intent]);
    }
  };

  const warpEdit = (intent: Intent | null) => {
    if (intent) onWarpEdit?.();
    one(intent);
  };

  const pullHandlers = modal
    ? {
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
          if ((e.target as HTMLElement).closest("button, input")) return;
          pullFrom.current = e.clientY;
          e.currentTarget.setPointerCapture(e.pointerId);
        },
        onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
          if (pullFrom.current !== null) setPull(Math.max(0, e.clientY - pullFrom.current));
        },
        onPointerUp: () => {
          pullFrom.current = null;
          if (pull > 90) onClose();
          setPull(0);
        },
        onPointerCancel: () => {
          pullFrom.current = null;
          setPull(0);
        },
      }
    : {};

  // What the clip is, as the timeline's footer says it.
  const rendered = renderStatus(clip);
  const meta = `${lengthLabel(clip.duration, beatsPerBar)} · ${warped ? `warped at ${Math.round(sourceBpm * 10) / 10} BPM` : "unwarped"}`;

  const panel = (() => {
    switch (tab) {
      case "warp":
        return (
          <WarpPanel
            choice={choice}
            warped={warped}
            sourceBpm={sourceBpm}
            ownMarkers={ownMarkers}
            onChoice={(c) => one(setClipWarp(room, clip.id, c === "complex" && !warped ? warpOnKeeping(clip.warpModeValue) : warpFor(c)))}
            settings={clip.warpSettings ?? {}}
            onSettings={(patch) => warpEdit(setClipWarpSettings(room, clip.id, patch))}
            color={color}
            onTempo={(v) => one(setClipFields(room, clip.id, { sourceTempo: Math.min(999, Math.max(20, v)) }))}
            onClearMarkers={() => warpEdit(setClipWarpMarkers(room, clip.id, []))}
          />
        );
      case "pitch":
        return (
          <PitchPanel
            transpose={clip.transpose ?? 0}
            detune={clip.detune ?? 0}
            choice={choice}
            color={color}
            onPitch={(p) => one(setClipPitch(room, clip.id, p))}
          />
        );
      case "loop":
        return (
          <LoopPanel
            warped={warped}
            looping={clip.looping}
            loopStart={clip.loopStart}
            loopEnd={clip.loopEnd}
            startMarker={clip.startMarker}
            endMarker={clip.endMarker}
            beatsPerBar={beatsPerBar}
            onLooping={(on) => one(setClipLooping(room, clip.id, on))}
            onLoop={(s, e) => one(setClipLoop(room, clip.id, s, e, MIN_LOOP_BEATS))}
            onWarpOn={() => one(setClipWarp(room, clip.id, warpOnKeeping(clip.warpModeValue)))}
          />
        );
      case "clip":
        return (
          <ClipPanel
            key={clip.id}
            name={clip.name}
            color={clip.color}
            muted={clip.muted}
            canBounce={!!url}
            onName={(name) => one(setClipFields(room, clip.id, { name }))}
            onColor={(c) => one(setClipFields(room, clip.id, { color: c }))}
            onMuted={(m) => one(setClipFields(room, clip.id, { muted: m }))}
            onBounce={onBounce}
          />
        );
    }
  })();

  const tablist = (
    <div className={cn(column && "z-10 bg-sunk/95 backdrop-blur", modal && "sticky top-0")}>
      <EditorTabs tabs={TABS} value={tab} onChange={setTab} column={column} label="Clip settings" />
    </div>
  );

  return (
    <>
      {modal && (
        <div className="fade-in fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]" onClick={onClose} aria-hidden />
      )}
      <section
        ref={sheetRef}
        role="dialog"
        aria-modal={modal || undefined}
        aria-label={`Edit clip: ${clip.name}`}
        tabIndex={-1}
        // The transport's Space still works in here (RoomPage).
        data-transport-keys
        onKeyDown={onSheetKey}
        style={pull > 0 ? { transform: `translateY(${pull}px)` } : undefined}
        className={cn(
          "flex flex-col overflow-hidden bg-sunk outline-none",
          modal
            ? cn(
                "sheet-up fixed inset-x-0 bottom-0 z-50 h-[94dvh] rounded-t-3xl shadow-2xl ring-1 ring-white/10",
                "pb-[env(safe-area-inset-bottom)]",
                pull === 0 && "transition-transform duration-200",
              )
            : presentation === "sheet"
              ? "min-h-0 flex-1"
              : "h-[42dvh] min-h-[280px] shrink-0 border-t border-panelline",
        )}
      >
        {/* The clip's colour washes the top of a sheet, so you know what you
            are holding. In the page's own sheet the page draws the strip —
            name, undo, play, close — so there is one header, not two. */}
        {presentation === "docked" && (
          <EditorHeader
            color={color}
            name={clip.name}
            muted={clip.muted}
            render={rendered === "none" ? undefined : rendered}
            meta={meta}
            actions={actions}
            onPreview={onPreview}
            previewing={previewing}
            history={history}
            onDownload={onDownload}
            onMinimize={onMinimize}
            onClose={onClose}
          />
        )}
        {modal && (
          <div
            className="shrink-0 select-none"
            style={{ background: `linear-gradient(180deg, ${color}40 0%, transparent 100%)` }}
            {...pullHandlers}
          >
            <div className="flex h-5 cursor-grab touch-none items-center justify-center" aria-hidden>
              <span className="h-1.5 w-12 rounded-full bg-white/30" />
            </div>
            <header className="flex items-center gap-1 px-3 pb-2">
              <span className="size-5 shrink-0 rounded-md shadow-inner" style={{ backgroundColor: color }} />
              <h2 className="min-w-0 flex-1 truncate pl-1.5 text-base font-semibold" title={clip.name}>
                {clip.name || "Clip"}
              </h2>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close clip editor"
                className="flex size-11 items-center justify-center rounded-lg text-foreground/80 hover:bg-white/5 [&_svg]:size-5"
              >
                <X />
              </button>
            </header>
          </div>
        )}

        {/* The page's sheet: the tabs stay put above the scrolling column, so
            Warp / Pitch / Loop / Clip are always in reach however short the
            sheet is; the waveform and the panel scroll under them. */}
        {presentation === "sheet" && <div className="shrink-0 border-t border-panelline">{tablist}</div>}

        {/* A sheet scrolls as one column (waveform first); docked, the
            controls sit on the left and the waveform takes the rest, as in
            Live. */}
        <div
          className={cn(
            "flex min-h-0 flex-1",
            column ? "flex-col overflow-y-auto overscroll-contain" : "flex-row-reverse",
          )}
        >
          {/* In the page's sheet the waveform takes what the tab's panel
              leaves — small at half, so the controls are in view; tall at
              full — and never less than a strip you can still grab. */}
          <div className={cn("flex min-w-0 flex-col", presentation === "sheet" ? "flex-[1_0_auto]" : column ? "shrink-0" : "min-h-0 flex-1")}>
            {url && file ? (
              <WarpWaveform
                key={clip.id}
                url={url}
                fileSec={file.sec}
                transients={transients}
                player={player}
                warped={warped}
                ownMarkers={clip.warpMarkers}
                sourceBpm={warped ? sourceBpm : tempo}
                startMarker={warped ? clip.startMarker : 0}
                endMarker={warped ? clip.endMarker : clip.duration}
                loop={warped && clip.looping ? { start: clip.loopStart, end: clip.loopEnd } : null}
                beatsPerBar={beatsPerBar}
                color={color}
                playheadBeat={playheadBeat}
                modal={column}
                sheet={presentation === "sheet"}
                onCommit={(markers) => warpEdit(setClipWarpMarkers(room, clip.id, markers))}
                onWarpOn={() => one(setClipWarp(room, clip.id, warpOnKeeping(clip.warpModeValue)))}
                warpCta={tab === "pitch" || tab === "clip"}
              />
            ) : (
              <div
                className={cn(
                  "flex items-center justify-center px-6 text-center text-sm text-muted-foreground",
                  presentation === "sheet" ? "h-[20dvh] min-h-[110px]" : column ? "h-[28dvh] min-h-[160px]" : "min-h-[140px] flex-1",
                )}
              >
                {!url ? "This clip has no audio to edit." : failed ? "Couldn't load this clip's audio." : "Loading audio…"}
              </div>
            )}
          </div>

          <div
            className={cn(
              "flex shrink-0 flex-col",
              column ? "border-t border-panelline" : "min-h-0 w-[300px] border-r border-panelline",
            )}
          >
            {presentation !== "sheet" && tablist}
            <div
              role="tabpanel"
              className={cn("px-3 text-xs", column ? "py-4" : "min-h-0 overflow-y-auto py-2.5")}
            >
              {panel}
            </div>
          </div>
        </div>

      </section>
    </>
  );
}

const WARP_HINTS: Record<WarpChoice, string> = {
  off: "Plays at its own speed, whatever the room's tempo.",
  beats: "For drums and loops: every hit stays as sharp as the file. Slowed down, each hit's tail repeats until the next one.",
  tones: "Not built for the browser yet: plays as Complex here. Live uses the real Tones mode.",
  texture: "Not built for the browser yet: plays as Complex here. Live uses the real Texture mode.",
  repitch: "Follows the room's tempo by speeding up or slowing down, so the pitch moves with it.",
  complex: "Follows the room's tempo; the pitch stays put. For anything: songs, chords, pads.",
  pro: "Complex, and a transpose keeps the sound's character (its formants) instead of shrinking or growing it.",
};

// ---------------------------------------------------------------------------
// The tabs
// ---------------------------------------------------------------------------

/** Live's Preserve choices, by the division in beats (0 = transients). */
const PRESERVE_LABELS: ReadonlyArray<{ value: string; label: string }> = WARP_PRESERVE_DIVISIONS.map((d) => ({
  value: String(d),
  label: d === 0 ? "Transients" : d === 4 ? "1 Bar" : `1/${Math.round(4 / d)}`,
}));

function WarpPanel({
  choice,
  warped,
  sourceBpm,
  ownMarkers,
  settings,
  color,
  onChoice,
  onSettings,
  onTempo,
  onClearMarkers,
}: {
  choice: WarpChoice;
  warped: boolean;
  sourceBpm: number;
  ownMarkers: boolean;
  settings: WarpSettingsData;
  color: string;
  onChoice: (c: WarpChoice) => void;
  onSettings: (patch: WarpSettingsData) => void;
  onTempo: (bpm: number) => void;
  onClearMarkers: () => void;
}) {
  const d = WARP_SETTINGS_DEFAULTS;
  return (
    <div className="flex flex-col gap-4">
      <Field label="Warp" hint={warped ? undefined : WARP_HINTS.off}>
        <BigChoice<"off" | "on">
          value={warped ? "on" : "off"}
          onChange={(v) => onChoice(v === "off" ? "off" : "complex")}
          options={[
            { value: "off", label: "Off" },
            { value: "on", label: "On" },
          ]}
        />
      </Field>
      {warped && (
        <Field label="Mode" hint={WARP_HINTS[choice]}>
          <BigChoice<WarpChoice>
            value={choice}
            onChange={onChoice}
            columns={3}
            label="Warp mode"
            options={WARP_CHOICES.map((m) => ({ value: m.choice, label: m.label }))}
          />
        </Field>
      )}
      {warped && choice === "beats" && (
        <>
          <Field label="Preserve" hint="Where the audio is cut into slices: at every hit, or on a grid division.">
            <BigChoice<string>
              value={String(settings.preserve ?? d.preserve)}
              onChange={(v) => onSettings({ preserve: Number(v) })}
              columns={4}
              label="Preserve"
              options={PRESERVE_LABELS}
            />
          </Field>
          <Field
            label="Loop"
            hint={
              (settings.loopMode ?? d.loopMode) === 0
                ? "A slice plays once, then silence until the next."
                : (settings.loopMode ?? d.loopMode) === 1
                  ? "A slice plays to its end, then its second half repeats forwards until the next."
                  : "A slice plays to its end, then its second half repeats backwards and forwards until the next. Live's default."
            }
          >
            <BigChoice<"0" | "1" | "2">
              value={String(settings.loopMode ?? d.loopMode) as "0" | "1" | "2"}
              onChange={(v) => onSettings({ loopMode: Number(v) as 0 | 1 | 2 })}
              label="Transient loop mode"
              options={[
                { value: "0", label: "Off" },
                { value: "1", label: "Forward" },
                { value: "2", label: "Back & forth" },
              ]}
            />
          </Field>
          <RangeField
            label="Envelope"
            hint="Decays every slice: 100 lets it ring on, 80 trims the tail, 50 is a tight gate, lower is tighter — at the clip's own tempo too. Double-tap to reset."
            min={0}
            max={100}
            step={1}
            unit="%"
            value={settings.envelope ?? d.envelope}
            reset={d.envelope}
            color={color}
            onCommit={(v) => onSettings({ envelope: v })}
          />
        </>
      )}
      {warped && choice === "pro" && (
        <>
          <RangeField
            label="Formants"
            hint="At 100 a transposed voice keeps its character; at 0 the formants move with the pitch, as in Complex. Double-tap to reset."
            min={0}
            max={100}
            step={1}
            unit="%"
            value={settings.formants ?? d.formants}
            reset={d.formants}
            color={color}
            onCommit={(v) => onSettings({ formants: v })}
          />
          <RangeField
            label="Envelope"
            hint="How finely the sound's envelope is followed: lower for high-pitched material, higher for low. 128 suits most. Double-tap to reset."
            min={MIN_PRO_ENVELOPE}
            max={MAX_PRO_ENVELOPE}
            step={8}
            value={settings.proEnvelope ?? d.proEnvelope}
            reset={d.proEnvelope}
            color={color}
            onCommit={(v) => onSettings({ proEnvelope: v })}
          />
        </>
      )}
      {warped && (
        <Field label="Clip tempo" hint={ownMarkers ? "Set by the warp markers." : "The tempo the audio was played at. Tap along to find it."}>
          <div className="flex flex-wrap items-center gap-1.5">
            <Stepper
              value={sourceBpm}
              step={1}
              min={20}
              max={999}
              decimals={2}
              unit="BPM"
              label="Clip tempo"
              disabled={ownMarkers}
              onCommit={onTempo}
            />
            <TextButton disabled={ownMarkers} onClick={() => onTempo(sourceBpm / 2)} label="Half the tempo">
              ÷2
            </TextButton>
            <TextButton disabled={ownMarkers} onClick={() => onTempo(sourceBpm * 2)} label="Double the tempo">
              ×2
            </TextButton>
          </div>
          {!ownMarkers && <TapTempo onTempo={onTempo} />}
          {ownMarkers && (
            <TextButton className="mt-2 self-start" onClick={onClearMarkers} label="Remove every warp marker">
              Clear markers
            </TextButton>
          )}
        </Field>
      )}
    </div>
  );
}

/** A knob as a big slider: drafts while dragging, commits on release (one
 * undo step), double-tap resets. */
function RangeField({
  label,
  hint,
  min,
  max,
  step,
  unit,
  value,
  reset,
  color,
  onCommit,
}: {
  label: string;
  hint: string;
  min: number;
  max: number;
  step: number;
  unit?: string;
  value: number;
  reset: number;
  color: string;
  onCommit: (v: number) => void;
}) {
  const [draft, setDraft] = useState<number | null>(null);
  const shown = draft ?? value;
  const commit = (): void => {
    if (draft !== null && draft !== value) onCommit(draft);
    setDraft(null);
  };
  return (
    <Field label={label} hint={hint}>
      <div className="flex items-center gap-3">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={shown}
          aria-label={label}
          onChange={(e) => setDraft(Number(e.target.value))}
          onPointerUp={commit}
          onKeyUp={commit}
          onBlur={commit}
          onDoubleClick={() => {
            setDraft(null);
            if (value !== reset) onCommit(reset);
          }}
          className="big-range h-11 min-w-0 flex-1 wide:h-6"
          style={{ accentColor: color }}
        />
        <span className="tabular w-14 text-right text-sm font-semibold">
          {shown}
          {unit ?? ""}
        </span>
      </div>
    </Field>
  );
}

/**
 * Tap along with the clip and the clip's tempo follows (a sampler's tap
 * pad). The tempo shows as you tap, and is proposed once, when you stop —
 * one undo step, not one per tap.
 */
function TapTempo({ onTempo }: { onTempo: (bpm: number) => void }) {
  const taps = useRef<number[]>([]);
  const [bpm, setBpm] = useState<number | null>(null);
  const [flash, setFlash] = useState(0);
  const timer = useRef<number | undefined>(undefined);
  const commit = useRef(onTempo);
  commit.current = onTempo;
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const tap = () => {
    const now = performance.now();
    const last = taps.current[taps.current.length - 1];
    if (last !== undefined && now - last > 2000) taps.current = [];
    taps.current = [...taps.current, now].slice(-6);
    setFlash((f) => f + 1);
    buzz(10);
    const t = taps.current;
    if (t.length < 2) {
      setBpm(null);
    } else {
      const avg = (t[t.length - 1]! - t[0]!) / (t.length - 1);
      const next = Math.round((60000 / avg) * 100) / 100;
      setBpm(next);
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => {
        taps.current = [];
        if (next >= 20 && next <= 999) commit.current(next);
        setBpm(null);
      }, 1500);
    }
  };
  return (
    <button
      type="button"
      onPointerDown={(e) => {
        // On the press, not the click: a tap tempo is about when.
        e.preventDefault();
        tap();
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") tap();
      }}
      aria-label="Tap tempo"
      className="mt-2 flex h-14 w-full touch-manipulation select-none items-center justify-between rounded-xl border border-dashed border-white/20 px-4 text-left active:bg-white/10 wide:h-10"
    >
      <span className="flex items-center gap-2 text-sm font-semibold">
        <Hand className="size-4" /> Tap tempo
      </span>
      <span key={flash} className="pop tabular text-sm font-bold text-primary">
        {bpm !== null ? `${bpm.toFixed(1)} BPM` : taps.current.length === 1 ? "keep tapping…" : ""}
      </span>
    </button>
  );
}

function PitchPanel({
  transpose,
  detune,
  choice,
  color,
  onPitch,
}: {
  transpose: number;
  detune: number;
  choice: WarpChoice;
  color: string;
  onPitch: (p: { transpose?: number; detune?: number }) => void;
}) {
  const locked = choice === "repitch";
  const [fine, setFine] = useState<number | null>(null);
  const shownFine = fine ?? detune;
  const commitFine = () => {
    if (fine === null) return;
    setFine(null);
    if (fine !== detune) onPitch({ detune: fine });
  };
  const signed = (v: number) => (v > 0 ? `+${v}` : v < 0 ? `−${-v}` : "0");
  return (
    <div className={cn("flex flex-col gap-4", locked && "opacity-40")}>
      {locked && (
        <p className="text-[11px] leading-snug text-muted-foreground">
          Re-Pitch: the clip's speed sets its pitch. Switch Warp to Keep pitch or Off to transpose it.
        </p>
      )}
      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={locked || transpose <= -MAX_TRANSPOSE}
          onClick={() => onPitch({ transpose: transpose - 1 })}
          aria-label="Down a semitone"
          className={cn("flex size-14 shrink-0 items-center justify-center rounded-2xl active:scale-95 disabled:opacity-30 wide:size-9 wide:rounded-lg [&_svg]:size-6 wide:[&_svg]:size-4", IDLE)}
        >
          <Minus />
        </button>
        <div className="flex min-w-0 flex-1 flex-col items-center">
          <span key={transpose} className="pop tabular text-5xl font-black leading-none wide:text-3xl" style={{ color }}>
            {signed(transpose)}
          </span>
          <span className="mt-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
            semitones{detune !== 0 ? ` · ${signed(detune)} ct` : ""}
          </span>
        </div>
        <button
          type="button"
          disabled={locked || transpose >= MAX_TRANSPOSE}
          onClick={() => onPitch({ transpose: transpose + 1 })}
          aria-label="Up a semitone"
          className={cn("flex size-14 shrink-0 items-center justify-center rounded-2xl active:scale-95 disabled:opacity-30 wide:size-9 wide:rounded-lg [&_svg]:size-6 wide:[&_svg]:size-4", IDLE)}
        >
          <Plus />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1.5">
        {INTERVALS.map((iv) => (
          <button
            key={iv.st}
            type="button"
            disabled={locked}
            onClick={() => onPitch({ transpose: iv.st, ...(iv.st === 0 ? { detune: 0 } : {}) })}
            aria-label={iv.name}
            title={iv.name}
            className={cn(
              "tabular h-12 rounded-xl text-sm font-bold active:scale-95 transition-transform wide:h-8 wide:text-xs",
              transpose === iv.st ? ACTIVE : IDLE,
            )}
          >
            {iv.label}
          </button>
        ))}
      </div>
      <Field label="Fine" hint="In cents. Double-tap the slider to centre it.">
        <div className="flex items-center gap-3">
          <input
            type="range"
            min={-MAX_DETUNE}
            max={MAX_DETUNE}
            step={1}
            disabled={locked}
            value={shownFine}
            aria-label="Fine tune in cents"
            onChange={(e) => setFine(Number(e.target.value))}
            onPointerUp={commitFine}
            onKeyUp={commitFine}
            onBlur={commitFine}
            onDoubleClick={() => {
              setFine(null);
              if (detune !== 0) onPitch({ detune: 0 });
            }}
            className="big-range h-11 min-w-0 flex-1 wide:h-6"
            style={{ accentColor: color }}
          />
          <span className="tabular w-12 text-right text-sm font-semibold">{signed(shownFine)} ct</span>
        </div>
      </Field>
    </div>
  );
}

function LoopPanel({
  warped,
  looping,
  loopStart,
  loopEnd,
  startMarker,
  endMarker,
  beatsPerBar,
  onLooping,
  onLoop,
  onWarpOn,
}: {
  warped: boolean;
  looping: boolean;
  loopStart: number;
  loopEnd: number;
  startMarker: number;
  endMarker: number;
  beatsPerBar: number;
  onLooping: (on: boolean) => void;
  onLoop: (start: number, end: number) => void;
  onWarpOn: () => void;
}) {
  if (!warped) {
    return (
      <div className="flex flex-col items-start gap-2">
        <p className="text-[11px] leading-snug text-muted-foreground">A clip loops once it follows the beat.</p>
        <button type="button" onClick={onWarpOn} className="h-11 rounded-full bg-[#f2c230] px-4 text-sm font-semibold text-black wide:h-8 wide:text-xs">
          Warp this clip to the beat
        </button>
      </div>
    );
  }
  const len = loopEnd - loopStart;
  const room = endMarker - startMarker;
  const lengths = [1, 2, beatsPerBar, beatsPerBar * 2, beatsPerBar * 4, beatsPerBar * 8].filter(
    (v, i, a) => a.indexOf(v) === i,
  );
  const lengthLabel = (beats: number) =>
    beats % beatsPerBar === 0 ? `${beats / beatsPerBar} bar${beats / beatsPerBar > 1 ? "s" : ""}` : `${beats} beat${beats > 1 ? "s" : ""}`;
  const shift = (d: number) => {
    const s = Math.max(startMarker, Math.min(endMarker - len, loopStart + d));
    if (s !== loopStart) onLoop(s, s + len);
  };
  return (
    <div className="flex flex-col gap-4">
      <button
        type="button"
        role="switch"
        aria-checked={looping}
        onClick={() => onLooping(!looping)}
        className={cn(
          "flex h-14 items-center justify-center gap-2 rounded-2xl text-base font-bold transition-colors wide:h-9 wide:text-sm",
          looping ? ACTIVE : IDLE,
        )}
      >
        <Repeat className="size-5 wide:size-4" /> {looping ? "Looping" : "Loop off"}
      </button>
      {looping && (
        <>
          <Field label="Length">
            <div className="grid grid-cols-3 gap-1.5">
              {lengths.map((b) => {
                const fits = b <= room + 1e-6;
                const on = Math.abs(len - b) < 1e-6;
                return (
                  <button
                    key={b}
                    type="button"
                    disabled={!fits}
                    onClick={() => onLoop(Math.min(loopStart, endMarker - b), Math.min(loopStart, endMarker - b) + b)}
                    className={cn(
                      "h-12 rounded-xl text-sm font-semibold active:scale-95 transition-transform disabled:opacity-25 wide:h-8 wide:text-xs",
                      on ? ACTIVE : IDLE,
                    )}
                  >
                    {lengthLabel(b)}
                  </button>
                );
              })}
            </div>
          </Field>
          <Field label="Move the loop" hint="By a beat. The length stays.">
            <div className="flex items-center gap-1.5">
              <TextButton label="Loop a beat earlier" onClick={() => shift(-1)} disabled={loopStart <= startMarker}>
                <ChevronLeft className="size-4" />
              </TextButton>
              <Stepper
                value={loopStart}
                step={1}
                min={startMarker}
                max={endMarker - MIN_LOOP_BEATS}
                label="Loop start in beats"
                prefix="from"
                onCommit={(v) => onLoop(v, v + len)}
              />
              <TextButton label="Loop a beat later" onClick={() => shift(1)} disabled={loopEnd >= endMarker}>
                <ChevronRight className="size-4" />
              </TextButton>
            </div>
          </Field>
          <Field label="Exact length" hint="In beats.">
            <Stepper
              value={len}
              step={1}
              min={MIN_LOOP_BEATS}
              max={room}
              label="Loop length in beats"
              prefix="for"
              onCommit={(v) => onLoop(loopStart, loopStart + v)}
            />
          </Field>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The waveform
// ---------------------------------------------------------------------------

interface WarpWaveformProps {
  url: string;
  fileSec: number;
  transients: readonly number[];
  player: ClipEditorPlayer;
  warped: boolean;
  ownMarkers: readonly WarpMarker[] | undefined;
  sourceBpm: number;
  startMarker: number;
  endMarker: number;
  loop: { start: number; end: number } | null;
  beatsPerBar: number;
  color: string;
  playheadBeat: number | null;
  /** On a phone's sheet: taller, and drawn for a thumb. */
  modal: boolean;
  /** In the page's own sheet: shorter still, so the tabs and the panel fit. */
  sheet?: boolean;
  onCommit: (markers: WarpMarker[]) => void;
  /** Turn warping on (the unwarped hint's button). */
  onWarpOn: () => void;
  /** Show that button. Off where the open tab already offers warping (Warp,
   * Loop), so the same offer is never on screen twice. */
  warpCta?: boolean;
}

type Hover = EditorPaint["hover"];
type DragMode = "stretch" | "slide";

/** One pointer's gesture. */
type Gesture =
  | {
      kind: "marker";
      pointerId: number;
      index: number;
      mode: DragMode;
      startX: number;
      /** The markers the drag started from — with a grabbed hit's new marker
       * in them, which only becomes real if the drag moves. */
      origin: WarpMarker[];
      moved: boolean;
    }
  | { kind: "pan"; pointerId: number; startX: number; start: ClipView; moved: boolean }
  | { kind: "pinch"; ids: [number, number]; startMid: number; startDist: number; start: ClipView };

/** How far a pointer may wander and still be a tap. */
const TAP_SLOP_PX = 6;
const DOUBLE_TAP_MS = 320;

function WarpWaveform({
  url,
  fileSec,
  transients,
  player,
  warped,
  ownMarkers,
  sourceBpm,
  startMarker,
  endMarker,
  loop,
  beatsPerBar,
  color,
  playheadBeat,
  modal,
  sheet = false,
  warpCta = true,
  onCommit,
  onWarpOn,
}: WarpWaveformProps) {
  const coarse = useCoarsePointer();
  const grabPx = coarse ? 22 : 7;
  const markerH = coarse ? 26 : MARKER_H;
  // Hits are dense, so they are grabbed only from the band along the
  // waveform's top where their ticks are drawn; everywhere else a drag
  // pans. Markers are grabbed along their whole line.
  const tickBand = coarse ? 40 : 22;

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  const committed = useMemo(() => effectiveMarkers(ownMarkers, fileSec, sourceBpm), [ownMarkers, fileSec, sourceBpm]);
  const [draft, setDraft] = useState<WarpMarker[] | null>(null);
  const markers = draft ?? committed;
  const [selected, setSelected] = useState<number | null>(null);
  const [hover, setHover] = useState<Hover>(null);
  const [menu, setMenu] = useState<{ x: number; y: number; index: number } | null>(null);
  const [mode, setMode] = useState<DragMode>("stretch");
  const [snap, setSnap] = useState(true);
  const gesture = useRef<Gesture | null>(null);
  const pointers = useRef(new Map<number, { x: number; y: number; type: string }>());
  const lastTap = useRef<{ t: number; x: number } | null>(null);
  const lastPointerType = useRef("mouse");

  // What there is to look at: the whole object and the clip's markers.
  const bounds = useMemo(() => {
    const endBeat = beatAtFileSeconds(committed, fileSec, sourceBpm);
    const from = Math.min(0, startMarker, committed[0]?.beat ?? 0);
    return { from, to: Math.max(endMarker, endBeat, from + 1) };
  }, [committed, fileSec, sourceBpm, startMarker, endMarker]);
  const fitView = useCallback((): ClipView | null => {
    if (size.width === 0) return null;
    const pad = (bounds.to - bounds.from) * 0.02;
    return { startBeat: bounds.from - pad, pxPerBeat: size.width / (bounds.to - bounds.from + 2 * pad) };
  }, [size.width, bounds]);
  // Never scrolled or zoomed so far out that the clip is lost: at most half
  // the view past either end, and no further out than twice the whole clip.
  const [view, setViewRaw] = useState<ClipView | null>(null);
  const setView = useCallback(
    (next: ClipView | null | ((v: ClipView | null) => ClipView | null)) =>
      setViewRaw((prev) => {
        const v = typeof next === "function" ? next(prev) : next;
        if (!v || size.width === 0) return v;
        const pxPerBeat = Math.max(v.pxPerBeat, size.width / ((bounds.to - bounds.from) * 2));
        const visible = size.width / pxPerBeat;
        const startBeat = Math.min(bounds.to - visible / 2, Math.max(bounds.from - visible / 2, v.startBeat));
        return { startBeat, pxPerBeat };
      }),
    [size.width, bounds],
  );
  useEffect(() => {
    if (!view) setView(fitView());
  }, [view, fitView, setView]);
  // A resize (a phone turned, a window dragged) keeps the same beats in view.
  const lastWidth = useRef(0);
  useEffect(() => {
    const before = lastWidth.current;
    lastWidth.current = size.width;
    if (before > 0 && size.width > 0 && before !== size.width) {
      setView((v) => (v ? { ...v, pxPerBeat: (v.pxPerBeat * size.width) / before } : v));
    }
  }, [size.width, setView]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      if (entry) setSize({ width: Math.floor(entry.contentRect.width), height: Math.floor(entry.contentRect.height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // A selection the committed list no longer has goes (undo, a peer's edit).
  useEffect(() => {
    if (selected !== null && selected >= committed.length) setSelected(null);
  }, [committed, selected]);

  // The envelope under the view: the visible file span, widened to an
  // eighth of itself either side and snapped to that grid, so a small scroll
  // or a marker drag reuses the player's cached peaks.
  const { peaks, decodedVersion } = player;
  const fileAt = useCallback(
    (beat: number): number => (warped ? fileSecondsAt(markers, beat, sourceBpm) : (beat * 60) / sourceBpm),
    [warped, markers, sourceBpm],
  );
  const envWindow = useMemo(() => {
    if (!view || size.width === 0) return null;
    const a = fileAt(xToBeat(view, 0));
    const b = fileAt(xToBeat(view, size.width));
    const lo = Math.max(0, Math.min(a, b));
    const hi = Math.min(fileSec, Math.max(a, b));
    if (!(hi > lo)) return { from: 0, sec: fileSec };
    const chunk = 2 ** Math.ceil(Math.log2((hi - lo) / 8));
    const from = Math.max(0, Math.floor(lo / chunk) * chunk - chunk);
    const to = Math.min(fileSec, Math.ceil(hi / chunk) * chunk + chunk);
    return { from, sec: Math.max(chunk, to - from) };
  }, [view, size.width, fileAt, fileSec]);
  const env = useMemo(() => {
    if (!envWindow) return null;
    void decodedVersion; // the redraw signal once the object has decoded
    const dpr = window.devicePixelRatio || 1;
    return peaks(url, Math.ceil(size.width * dpr * 2), envWindow.from, envWindow.sec);
  }, [peaks, url, size.width, envWindow, decodedVersion]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !view || size.width === 0) return;
    const g = prepareCanvas(canvas, size.width, size.height);
    if (!g) return;
    paintEditor(g, {
      width: size.width,
      height: size.height,
      view,
      beatsPerBar,
      markers,
      warped,
      fallbackBpm: sourceBpm,
      fileSec,
      env,
      envFromSec: envWindow?.from ?? 0,
      envSec: envWindow?.sec ?? fileSec,
      transients,
      startMarker,
      endMarker,
      loop,
      selected,
      hover,
      color,
      markerH,
      tickBand,
    });
  }, [view, size, beatsPerBar, markers, warped, sourceBpm, fileSec, env, envWindow, transients, startMarker, endMarker, loop, selected, hover, color, markerH, tickBand]);

  // Wheel: ⌘/ctrl (or a trackpad pinch) zooms about the pointer; anything
  // else scrolls. Native and non-passive, so the page stays still.
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const x = e.clientX - el.getBoundingClientRect().left;
      setView((v) => {
        if (!v) return v;
        if (e.ctrlKey || e.metaKey) return zoomAt(v, x, Math.exp(-e.deltaY * 0.004));
        const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
        return { ...v, startBeat: v.startBeat + d / v.pxPerBeat };
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [setView]);

  const localX = (e: { clientX: number }): number => e.clientX - (canvasRef.current?.getBoundingClientRect().left ?? 0);
  const localY = (e: { clientY: number }): number => e.clientY - (canvasRef.current?.getBoundingClientRect().top ?? 0);

  /** What is under a point: a marker (anywhere on its line), else a hit in
   * the audio (on the waveform), else nothing. */
  const hitTest = useCallback(
    (x: number, y: number): Hover => {
      if (!view || !warped) return null;
      const within = grabPx / view.pxPerBeat;
      const i = markerNear(markers, xToBeat(view, x), within);
      if (i !== null) return { kind: "marker", index: i };
      if (y < RULER_H + markerH || y > RULER_H + markerH + tickBand) return null;
      const sec = fileAt(xToBeat(view, x));
      const secSpan = Math.abs(fileAt(xToBeat(view, x) + within) - sec);
      const t = nearestTransient(transients, sec, secSpan);
      return t === null ? null : { kind: "transient", sec: t };
    },
    [view, warped, grabPx, markers, markerH, tickBand, fileAt, transients],
  );

  const commitAndSelect = (next: { markers: WarpMarker[]; index: number } | null) => {
    if (!next) return;
    setSelected(next.index);
    onCommit(next.markers);
  };

  const deleteSelected = () => {
    if (selected === null) return;
    const next = removeMarker(markers, selected);
    if (!next) return;
    setSelected(null);
    onCommit(next);
  };

  /** Double-click / double-tap: on a marker deletes it; elsewhere adds one
   * (on the hit under the pointer, if there is one). */
  const doubleAt = (x: number, y: number) => {
    if (!view || !warped) return;
    const hit = hitTest(x, y);
    if (hit?.kind === "marker") {
      setSelected(hit.index);
      const next = removeMarker(markers, hit.index);
      if (next) {
        setSelected(null);
        onCommit(next);
      }
      return;
    }
    commitAndSelect(
      hit?.kind === "transient" ? insertMarkerAtSec(markers, hit.sec, sourceBpm) : insertMarkerAtBeat(markers, xToBeat(view, x), sourceBpm),
    );
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    if (e.button !== 0 || !view) return;
    lastPointerType.current = e.pointerType;
    setMenu(null);
    const x = localX(e);
    pointers.current.set(e.pointerId, { x, y: localY(e), type: e.pointerType });
    e.currentTarget.setPointerCapture(e.pointerId);

    // A second finger turns whatever the first was doing into a pinch.
    if (pointers.current.size === 2) {
      const [a, b] = [...pointers.current.entries()];
      setDraft(null);
      gesture.current = {
        kind: "pinch",
        ids: [a![0], b![0]],
        startMid: (a![1].x + b![1].x) / 2,
        startDist: Math.abs(a![1].x - b![1].x),
        start: view,
      };
      return;
    }
    if (pointers.current.size > 2) return;

    const hit = hitTest(x, localY(e));
    if (!hit) {
      gesture.current = { kind: "pan", pointerId: e.pointerId, startX: x, start: view, moved: false };
      return;
    }
    // Grabbing a hit drags a new marker out of it (Live's pseudo marker);
    // a touch that never moves leaves nothing behind.
    let origin = markers;
    let index: number;
    if (hit.kind === "marker") {
      index = hit.index;
      setSelected(index);
    } else {
      const ins = insertMarkerAtSec(markers, hit.sec, sourceBpm);
      if (!ins) return;
      origin = ins.markers;
      index = ins.index;
    }
    // Shift flips the toolbar's mode for this drag (a mouse's shortcut).
    const dragMode: DragMode = e.shiftKey ? (mode === "stretch" ? "slide" : "stretch") : mode;
    gesture.current = { kind: "marker", pointerId: e.pointerId, index, mode: dragMode, startX: x, origin, moved: false };
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const x = localX(e);
    const tracked = pointers.current.get(e.pointerId);
    if (tracked) tracked.x = x;
    const g = gesture.current;
    if (!g || !view) {
      if (e.pointerType === "mouse") setHover(hitTest(x, localY(e)));
      return;
    }
    if (g.kind === "pinch") {
      const a = pointers.current.get(g.ids[0]);
      const b = pointers.current.get(g.ids[1]);
      if (!a || !b) return;
      setView(pinchView(g.start, g.startMid, g.startDist, (a.x + b.x) / 2, Math.abs(a.x - b.x)));
      return;
    }
    if (g.pointerId !== e.pointerId) return;
    if (!g.moved && Math.abs(x - g.startX) < (e.pointerType === "mouse" ? 2 : TAP_SLOP_PX)) return;
    if (g.kind === "pan") {
      g.moved = true;
      setView({ ...g.start, startBeat: g.start.startBeat - (x - g.startX) / g.start.pxPerBeat });
      return;
    }
    if (!g.moved) setSelected(g.index);
    g.moved = true;
    const m = g.origin[g.index]!;
    const dBeats = (x - g.startX) / view.pxPerBeat;
    if (g.mode === "stretch") {
      // ⌥ flips the snap for this drag.
      const snapping = e.altKey ? !snap : snap;
      const target = m.beat + dBeats;
      setDraft(moveMarkerBeat(g.origin, g.index, snapping ? snapBeat(target, snapGrid(view.pxPerBeat)) : target));
    } else {
      // Slide: dragging right brings earlier audio under the marker.
      const eps = 1e-3;
      const secPerBeat = (fileSecondsAt(g.origin, m.beat + eps, sourceBpm) - fileSecondsAt(g.origin, m.beat - eps, sourceBpm)) / (2 * eps);
      setDraft(moveMarkerSec(g.origin, g.index, m.sec - dBeats * secPerBeat));
    }
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>, cancelled = false) => {
    pointers.current.delete(e.pointerId);
    const g = gesture.current;
    if (!g) return;
    if (g.kind === "pinch") {
      // Lifting one finger ends the pinch; the other does nothing more.
      gesture.current = null;
      return;
    }
    if (g.pointerId !== e.pointerId) return;
    gesture.current = null;
    if (g.kind === "marker") {
      if (!cancelled && g.moved && draft) onCommit(draft);
      setDraft(null);
    }
    if (cancelled || g.moved) return;
    const x = localX(e);
    // A tap on nothing lets go of the selection.
    if (g.kind === "pan") setSelected(null);
    // A finger's double-tap (a mouse has dblclick).
    if (e.pointerType !== "mouse") {
      const now = performance.now();
      const prev = lastTap.current;
      if (prev && now - prev.t < DOUBLE_TAP_MS && Math.abs(prev.x - x) < 24) {
        lastTap.current = null;
        doubleAt(x, localY(e));
      } else {
        lastTap.current = { t: now, x };
      }
    }
  };

  const onDoubleClick = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    // A double-tap was already handled on pointerup.
    if (lastPointerType.current !== "mouse") return;
    doubleAt(localX(e), localY(e));
  };

  const onContextMenu = (e: ReactMouseEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const hit = hitTest(localX(e), localY(e));
    if (hit?.kind !== "marker") return;
    setSelected(hit.index);
    setMenu({ x: localX(e), y: localY(e), index: hit.index });
  };

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && (menu || selected !== null)) {
      e.stopPropagation();
      if (menu) setMenu(null);
      else setSelected(null);
      return;
    }
    if ((e.key === "Delete" || e.key === "Backspace") && selected !== null) {
      // The page's own Delete removes the selected CLIPS; this one is ours.
      e.preventDefault();
      e.stopPropagation();
      deleteSelected();
    }
  };

  /** "+ Marker": at the playhead when it is in view, else mid-view, on the
   * nearest hit within reach. */
  const addMarker = () => {
    if (!view || !warped) return;
    const playX = playheadBeat !== null ? beatToX(view, playheadBeat) : null;
    const beat = playX !== null && playX >= 0 && playX <= size.width ? playheadBeat! : xToBeat(view, size.width / 2);
    const reachSec = Math.abs(fileAt(beat + 24 / view.pxPerBeat) - fileAt(beat));
    commitAndSelect(addMarkerNear(markers, beat, transients, reachSec, sourceBpm));
  };

  const zoomBy = (factor: number) => setView((v) => (v ? zoomAt(v, size.width / 2, factor) : v));

  const segBpm = selected !== null ? segmentBpm(markers, selected) : null;
  const playX = view && playheadBeat !== null ? beatToX(view, playheadBeat) : null;
  const canDelete = selected !== null && markers.length > 2;
  const canStraighten = selected !== null && selected >= 1 && selected < markers.length - 1;

  const hint = !warped
    ? null
    : selected !== null
      ? mode === "stretch"
        ? `Drag the marker to stretch the audio either side of it${segBpm !== null ? ` · this part runs at ${segBpm.toFixed(1)} BPM` : ""}.`
        : "Drag to slide the audio under the marker; the beat it sits on stays put."
      : !ownMarkers || ownMarkers.length < 3
        ? coarse
          ? "Drag a grey tick along the top (a hit in the audio) onto a beat line to line it up. Double-tap to add a marker; drag to scroll, pinch to zoom."
          : "Drag a grey tick along the top (a hit in the audio) onto a beat line to line it up. Double-click to add a marker; drag to scroll."
        : coarse
          ? "Tap a marker to select it; drag it to stretch. Double-tap one to remove it."
          : "Drag a marker to stretch. Double-click one to remove it. ⇧ slides, ⌥ skips the grid.";

  return (
    <div className={cn("flex flex-col", (!modal || sheet) && "min-h-0 flex-1")}>
      <div
        ref={wrapRef}
        tabIndex={0}
        onKeyDown={onKeyDown}
        className={cn(
          "relative shrink-0 outline-none focus-visible:ring-1 focus-visible:ring-[var(--ring)]",
          sheet ? "min-h-[96px] flex-1" : modal ? "h-[28dvh] min-h-[160px]" : "min-h-[140px] flex-1",
        )}
      >
        <canvas
          ref={canvasRef}
          aria-label="Clip waveform with warp markers"
          className={cn(
            "absolute inset-0 touch-none select-none",
            gesture.current?.kind === "pan" ? "cursor-grabbing" : hover ? "cursor-ew-resize" : "cursor-grab",
          )}
          style={{ width: size.width, height: size.height }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => onPointerUp(e)}
          onPointerCancel={(e) => onPointerUp(e, true)}
          onPointerLeave={() => {
            if (!gesture.current) setHover(null);
          }}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        />
        {playX !== null && playX >= 0 && playX <= size.width && (
          <div className="pointer-events-none absolute inset-y-0 w-px bg-white/80" style={{ left: playX }} />
        )}
        {!warped && warpCta && (
          <div className="absolute inset-x-0 bottom-3 flex justify-center">
            <button
              type="button"
              onClick={onWarpOn}
              className="rounded-full bg-[#f2c230] px-4 py-2 text-xs font-semibold text-black shadow-lg wide:py-1.5"
            >
              Warp this clip to the beat
            </button>
          </div>
        )}
        {menu && (
          <div
            role="menu"
            className="absolute z-10 min-w-[190px] rounded-md border border-panelline bg-raised py-1 text-xs shadow-lg"
            style={{ left: Math.max(0, Math.min(menu.x, size.width - 200)), top: Math.max(0, Math.min(menu.y, size.height - 70)) }}
          >
            <MenuItem
              disabled={markers.length <= 2}
              onClick={() => {
                setMenu(null);
                deleteSelected();
              }}
            >
              Delete warp marker
            </MenuItem>
            <MenuItem
              disabled={menu.index < 1 || menu.index >= markers.length - 1}
              onClick={() => {
                const next = warpStraightFrom(markers, menu.index);
                setMenu(null);
                if (next) onCommit(next);
              }}
            >
              Straighten everything after
            </MenuItem>
          </div>
        )}
      </div>

      {/* The toolbar: what a phone can't do with modifier keys. */}
      {warped && (
        <div className="flex shrink-0 flex-wrap items-center gap-1.5 border-t border-panelline px-2 py-2 wide:py-1.5">
          <ToolButton onClick={addMarker} label="Add a warp marker at the playhead or mid-view">
            <Plus /> Marker
          </ToolButton>
          <Segmented<DragMode>
            value={mode}
            onChange={setMode}
            options={[
              { value: "stretch", label: "Stretch", title: "Dragging a marker stretches the audio to meet it" },
              { value: "slide", label: "Slide", title: "Dragging a marker slides the audio under it" },
            ]}
          />
          <ToolButton onClick={() => setSnap((s) => !s)} active={snap} label={snap ? "Snapping to the grid (tap to drag freely)" : "Dragging freely (tap to snap to the grid)"}>
            <Magnet /> Snap
          </ToolButton>
          {selected !== null && (
            <>
              <ToolButton onClick={deleteSelected} disabled={!canDelete} label="Delete the selected marker" danger>
                <Trash2 /> <span className="hidden min-[420px]:inline">Delete</span>
              </ToolButton>
              {canStraighten && (
                <ToolButton
                  onClick={() => {
                    const next = warpStraightFrom(markers, selected);
                    if (next) onCommit(next);
                  }}
                  label="Remove every marker after this one, keeping this part's tempo to the end"
                >
                  Straighten after
                </ToolButton>
              )}
            </>
          )}
          <div className="ml-auto flex items-center gap-1">
            <ToolButton onClick={() => zoomBy(1 / 1.5)} label="Zoom out" iconOnly>
              <ZoomOut />
            </ToolButton>
            <ToolButton onClick={() => zoomBy(1.5)} label="Zoom in" iconOnly>
              <ZoomIn />
            </ToolButton>
            <ToolButton onClick={() => setView(fitView())} label="Show the whole clip" iconOnly>
              <Maximize2 />
            </ToolButton>
          </div>
        </div>
      )}
      {hint && (
        <p role="status" className="shrink-0 px-3 pb-2 text-[11px] leading-snug text-muted-foreground wide:pb-1.5">
          {hint}
        </p>
      )}
    </div>
  );
}

/** The grid a stretched marker snaps to: fine enough to be useful at this
 * zoom (a sixteenth when zoomed in, a beat when zoomed out). */
function snapGrid(pxPerBeat: number): number {
  return pxPerBeat >= 160 ? 1 / 16 : pxPerBeat >= 60 ? 1 / 8 : pxPerBeat >= 24 ? 1 / 4 : 1;
}

// ---------------------------------------------------------------------------
// Controls — every one at least 44px on a phone.
// ---------------------------------------------------------------------------

function MenuItem({ children, onClick, disabled }: { children: ReactNode; onClick: () => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      onClick={onClick}
      className="block w-full px-3 py-2 text-left hover:bg-white/5 disabled:opacity-40"
    >
      {children}
    </button>
  );
}
